import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useMutation, useQuery } from "@apollo/client";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import sfdaMedicine from "../../../gql/sfdaMedicine";
import CustomFilterInput from "../../../shared/CustomFilterInput";
import AppButton from "../../../shared/AppButton";
import { CircularProgress } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import UpdateIcon from "@mui/icons-material/Update";
import CloseIcon from "@mui/icons-material/Close";
import { FaCheckDouble } from "react-icons/fa";

function SfdaMedicineForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const item = location.state?.item;

  const [createMedicine, { loading: creating }] = useMutation(
    sfdaMedicine.CREATE_SFDA_MEDICINE
  );
  const [updateMedicine, { loading: updating }] = useMutation(
    sfdaMedicine.UPDATE_SFDA_MEDICINE
  );

  const initialValues = {
    sfdaId: item?.sfdaId || 0,
    registerNumber: item?.registerNumber || "",
    registerYear: item?.registerYear || 0,
    gtin: item?.gtin || "",
    drugType: item?.drugType || "",
    subType: item?.subType || "",
    scientificName: item?.scientificName || "",
    tradeName: item?.tradeName || "",
    strength: item?.strength || "",
    strengthUnit: item?.strengthUnit || "",
    pharmaceuticalForm: item?.pharmaceuticalForm || "",
    administrationRoute: item?.administrationRoute || "",
    size: item?.size || "",
    sizeUnit: item?.sizeUnit || "",
    packageTypes: item?.packageTypes || "",
    legalStatus: item?.legalStatus || "",
    productControl: item?.productControl || "",
    publicPrice: item?.publicPrice || "",
    marketingCompany: item?.marketingCompany || "",
    atcCode1: item?.atcCode1 || "",
    atcCode2: item?.atcCode2 || "",
    packageSize: item?.packageSize || 0,
    shelfLife: item?.shelfLife || 0,
    storageConditions: item?.storageConditions || "",
    marketingStatus: item?.marketingStatus || "",
    authorizationStatus: item?.authorizationStatus || "",
    manufacturerName: item?.manufacturerName || "",
    manufacturerCountry: item?.manufacturerCountry || "",
    additionalManufacturer: item?.additionalManufacturer || "",
    additionalManufacturerCountry: item?.additionalManufacturerCountry || "",
    companyCountryEn: item?.companyCountryEn || "",
    companyCountryAr: item?.companyCountryAr || "",
    legalStatusEn: item?.legalStatusEn || "",
    legalStatusAr: item?.legalStatusAr || "",
    domainEn: item?.domainEn || "",
    domainAr: item?.domainAr || "",
    imageUrl: item?.imageUrl || "",
    distributionArea: item?.distributionArea || "",
    drugCombinationsStrengthValue: item?.drugCombinationsStrengthValue || "",
    drugCombinationsScientificName: item?.drugCombinationsScientificName || "",
    drugCombinationsStrengthUnit: item?.drugCombinationsStrengthUnit || "",
    descriptionCode: item?.descriptionCode || "",
    productControlAr: item?.productControlAr || "",
    agent: item?.agent || "",
  };

  const validationSchema = Yup.object().shape({
    registerNumber: Yup.string().required(t("Register Number is required")),
    tradeName: Yup.string().required(t("Trade Name is required")),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      const { supergitMasterCode, id: _id, __typename, ...cleanValues } = values;
      const input = {
        ...cleanValues,
        sfdaId: parseInt(cleanValues.sfdaId) || 0,
        registerYear: parseInt(cleanValues.registerYear) || 0,
        packageSize: parseInt(cleanValues.packageSize) || 0,
        shelfLife: parseInt(cleanValues.shelfLife) || 0,
      };

      try {
        if (item?.id) {
          await updateMedicine({
            variables: { id: item.id, input },
          });
          toast.success(t("Medicine updated successfully"));
        } else {
          await createMedicine({
            variables: { input },
          });
          toast.success(t("Medicine created successfully"));
        }
        navigate("/super-admin/sfda-medicine");
      } catch (error) {
        toast.error(error.message);
      }
    },
  });

  const renderSection = (title, fields) => (
    <div className="personal-data !mb-3">
      <div className="personal-title !mb-2">
        <div className="inventory-svg">
          <svg
            width="26"
            height="26"
            viewBox="0 0 34 34"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="34" height="34" rx="8" fill="#F4F6F8" />
            <path
              d="M9.25 11H11.25V14H21.25V11H23.25V16H25.25V11C25.25 9.9 24.35 9 23.25 9H19.07C18.65 7.84 17.55 7 16.25 7C14.95 7 13.85 7.84 13.43 9H9.25C8.15 9 7.25 9.9 7.25 11V25C7.25 26.1 8.15 27 9.25 27H15.25V25H9.25V11ZM16.25 9C16.8 9 17.25 9.45 17.25 10C17.25 10.55 16.8 11 16.25 11C15.7 11 15.25 10.55 15.25 10C15.25 9.45 15.7 9 16.25 9Z"
              fill="#0070C2"
            />
            <path
              d="M25.25 17.5L19.76 23L16.75 20L15.25 21.5L19.76 26L26.75 19L25.25 17.5Z"
              fill="#0070C2"
            />
          </svg>
        </div>
        <div className="personal-head">
          <span>{t(title)}</span>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-1.5">
        {fields.map((field) => {
          const cleanLabel = field.label.replace("*", "").trim();
          const placeholderText = field.placeholder
            ? t(field.placeholder)
            : `${t("Enter")} ${t(cleanLabel)}`;
          return (
            <CustomFilterInput
              key={field.name}
              label={t(field.label)}
              name={field.name}
              type={field.type || "text"}
              placeholder={placeholderText}
              value={formik.values[field.name]}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors[field.name]}
              touched={formik.touched[field.name]}
            />
          );
        })}
      </div>
    </div>
  );

  const { i18n } = useTranslation();

  return (
    <div className="superGit_container">
      <div className="flex space-x-2 !mb-2">
        <div
          className="back flex items-center justify-center cursor-pointer transition-all duration-200 hover:opacity-80 active:scale-95"
          onClick={() => navigate("/super-admin/sfda-medicine")}
          style={{
            backgroundColor: "#0070C2",
            width: "24px",
            height: "24px",
            borderRadius: "50%",
            boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          {i18n.language === "en" ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M9 19L10.41 17.59L5.83 13H22V11H5.83L10.42 6.41L9 5L2 12L9 19Z"
                fill="white"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
            >
              <g transform="scale(-1,1) translate(-24,0)">
                <path
                  d="M9 19L10.41 17.59L5.83 13H22V11H5.83L10.42 6.41L9 5L2 12L9 19Z"
                  fill="white"
                />
              </g>
            </svg>
          )}
        </div>

        <div>
          <span className="font-semibold text-black/70 text-lg ">
            {item ? t("Edit SFDA Medicine") : t("Create SFDA Medicine")}
          </span>
        </div>
      </div>

      <form onSubmit={formik.handleSubmit}>
        <div className="benefeciary-create">
          {renderSection("Product Information", [
            { name: "sfdaId", label: "SFDA ID", type: "number" },
            { name: "registerNumber", label: "Register Number*" },
            { name: "registerYear", label: "Register Year", type: "number" },
            { name: "gtin", label: "GTIN" },
            { name: "tradeName", label: "Trade Name*" },
            { name: "scientificName", label: "Scientific Name" },
            { name: "descriptionCode", label: "Description Code" },
          ])}

          {renderSection("Dosage & Administration", [
            { name: "strength", label: "Strength" },
            { name: "strengthUnit", label: "Strength Unit" },
            { name: "pharmaceuticalForm", label: "Pharmaceutical Form" },
            { name: "administrationRoute", label: "Administration Route" },
            { name: "drugType", label: "Drug Type" },
            { name: "subType", label: "Sub Type" },
          ])}

          {renderSection("Packaging", [
            { name: "size", label: "Size" },
            { name: "sizeUnit", label: "Size Unit" },
            { name: "packageSize", label: "Package Size", type: "number" },
            { name: "packageTypes", label: "Package Types" },
          ])}

          {renderSection("Classification & Pricing", [
            { name: "legalStatus", label: "Legal Status" },
            { name: "legalStatusEn", label: "Legal Status (EN)" },
            { name: "legalStatusAr", label: "Legal Status (AR)" },
            { name: "productControl", label: "Product Control" },
            { name: "productControlAr", label: "Product Control (AR)" },
            { name: "publicPrice", label: "Public Price" },
            { name: "domainEn", label: "Domain (EN)" },
            { name: "domainAr", label: "Domain (AR)" },
            { name: "distributionArea", label: "Distribution Area" },
          ])}

          {renderSection("Manufacturer & Marketing", [
            { name: "marketingCompany", label: "Marketing Company" },
            { name: "marketingStatus", label: "Marketing Status" },
            { name: "authorizationStatus", label: "Authorization Status" },
            { name: "manufacturerName", label: "Manufacturer Name" },
            { name: "manufacturerCountry", label: "Manufacturer Country" },
            { name: "additionalManufacturer", label: "Additional Manufacturer" },
            {
              name: "additionalManufacturerCountry",
              label: "Addl Manufacturer Country",
            },
            { name: "companyCountryEn", label: "Company Country (EN)" },
            { name: "companyCountryAr", label: "Company Country (AR)" },
            { name: "agent", label: "Agent" },
          ])}

          {renderSection("Additional Details", [
            { name: "atcCode1", label: "ATC Code 1" },
            { name: "atcCode2", label: "ATC Code 2" },
            { name: "shelfLife", label: "Shelf Life (Months)", type: "number" },
            { name: "storageConditions", label: "Storage Conditions" },
            { name: "imageUrl", label: "Image URL" },
          ])}

          {renderSection("Combinations", [
            {
              name: "drugCombinationsScientificName",
              label: "Comb. Scientific Name",
            },
            { name: "drugCombinationsStrengthValue", label: "Comb. Strength Val" },
            { name: "drugCombinationsStrengthUnit", label: "Comb. Strength Unit" },
          ])}
        </div>

        <div className="flex justify-center gap-2 mt-4 pb-8">
          <AppButton
            label={t("Cancel")}
            onClick={() => navigate("/super-admin/sfda-medicine")}
            bgColor="#94a3b8"
            startIcon={<CloseIcon />}
          />
          <AppButton
            label={item ? t("Update") : t("Save")}
            type="submit"
            isLoading={creating || updating}
            startIcon={item ? <UpdateIcon /> : <FaCheckDouble />}
          />
        </div>
      </form>
    </div>
  );
}

export default SfdaMedicineForm;
